/**
 * What a request in words arranges.
 *
 * An answer in words sets the board and arranges it at once, but not the whole of it: a long
 * score asked for wrongly is minutes of arranging nobody wanted. So the first run of an answer
 * is a first hearing, the first `FIRST_HEARING` bars of the window asked for, and the rest is
 * arranged only when the reader says so, by pressing to go on or by asking again for nothing
 * new. A window no longer than that is arranged whole at once.
 *
 * An answer is compared with the run on screen through what each would send (`sentOf`), so a
 * board set by hand and the same board set in words are the same run, and a setting a run does
 * not send is no difference between two. Every run of the board, pressed or asked for, is a
 * `Ran` and sent as `requestOf` makes it.
 */

import type { AskPayload, AskSettings } from "@app/shared-types";
import { type Choices, NO_CHOICES } from "../components/ArrangeChoices";
import { askedOf, layersTold } from "../components/ArrangeHints";
import { requested, type Review } from "../components/ArrangeReview";
import { unseatable } from "../components/ArrangeStartMenu";
import type { Picked } from "../components/ArrangeToolbar";
import type { ArrangeAsk } from "./arrange-job";

/** How many bars an answer in words arranges first, where it asks for more. */
export const FIRST_HEARING = 16;

/** The settings on the board, as its toolbar holds them. */
export interface Board {
  readonly roster: readonly string[];
  readonly choices: Choices;
  readonly review: Review;
  /** Every bar, or the window typed into `first` and `last`. */
  readonly whole: boolean;
  readonly first: string;
  readonly last: string;
}

/** A run arranged, as an answer in words is compared with it. */
export interface Ran {
  readonly board: Board;
  /** The bars sent, or `undefined` for the whole piece. */
  readonly bars: [number, number] | undefined;
  readonly take: number;
  /** Whether it arranged only the first bars of the board's window. */
  readonly preview: boolean;
}

/** The bar window as the route wants it, or `undefined` for the whole piece. */
export function windowOf(first: string, last: string): [number, number] | undefined {
  if (first.trim() === "" && last.trim() === "") return undefined;
  const from = first.trim() === "" ? 1 : Number(first);
  // A last bar past the end is clipped by the reader, so an open end can ask for more than
  // the score has and be told which bars it actually got.
  const to = last.trim() === "" ? 100000 : Number(last);
  return [from, to];
}

/** The window `board` asks for, or `undefined` for the whole piece. */
export function askedWindow(board: Board): [number, number] | undefined {
  return board.whole ? undefined : windowOf(board.first, board.last);
}

/** A bar typed into the toolbar, or `fallback` where the field holds no bar number. */
function barOf(typed: string, fallback: number): number {
  const bar = Number(typed.trim());
  return typed.trim() !== "" && Number.isInteger(bar) && bar >= 1 ? bar : fallback;
}

/** Every setting of `board` as a request in words is read against, in a score of `total` bars. */
export function settingsOf(board: Board, total: number | null): AskSettings {
  const { choices, review } = board;
  const start = barOf(board.first, 1);
  return {
    ...choices,
    whole: board.whole,
    first: start,
    last: barOf(board.last, total ?? start),
    start: review.start,
    layers: [...layersTold(review.told)],
    strength: review.told.strength,
    more: review.told.hints.includes("more") ? review.told.more : null,
    ...review.wishes,
    playingWho: [...review.wishes.playingWho],
  };
}

/**
 * `board` as `answer` sets it, the wishes among them. The AI arranger's level and the check are
 * not settings an answer reads or writes, so they stay as they were, and so does what is held,
 * whoever its players are.
 */
export function boardOf(board: Board, { roster, settings }: AskPayload): Board {
  const { whole, first, last, start, layers, strength, more, ...rest } = settings;
  const { playing, playingWho, ease, key, ...choices } = rest;
  const { review } = board;
  return {
    roster,
    choices,
    review: {
      ...review,
      start,
      told: askedOf(review.told, layers, strength, more),
      wishes: { playing, playingWho, ease, key },
    },
    whole,
    first: String(first),
    last: String(last),
  };
}

/**
 * What a run of `board` sends of it, whatever its take: the players, the bars, and the choices
 * and the review as `requestOf` sends them, in one order of keys. A board set by an answer holds
 * its choices in the order the server wrote them, and one set by hand in the toolbar's.
 */
function sentOf(board: Board, total: number | null): string {
  const { whole, first, last } = settingsOf(board, total);
  const { review } = board;
  const sent = { ...(review.start === "score" ? board.choices : NO_CHOICES), ...requested(review) };
  const ordered = Object.entries(sent).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify([board.roster, whole, first, last, ordered]);
}

/** Whether two boards arrange the same, player for player and setting for setting. */
function sameAs(board: Board, other: Board, total: number | null): boolean {
  return sentOf(board, total) === sentOf(other, total);
}

/**
 * The bars a first hearing of `window` arranges in a score of `total` bars, or `null` where the
 * window is short enough to arrange whole at once.
 */
export function firstHearing(
  window: [number, number] | undefined,
  total: number | null,
): [number, number] | null {
  const from = window?.[0] ?? 1;
  const to = Math.min(window?.[1] ?? Infinity, total ?? Infinity);
  return to - from + 1 > FIRST_HEARING ? [from, from + FIRST_HEARING - 1] : null;
}

/** The bars of `window` in words, in a score of `total` bars. */
export function barsSaid(window: [number, number] | undefined, total: number | null): string {
  if (window === undefined) return total === null ? "the whole score" : `all ${total} bars`;
  return `bars ${window[0]}–${total === null ? window[1] : Math.min(window[1], total)}`;
}

/**
 * Why an answer that set `board` arranges nothing, or `null` where it may: no score is `opened`,
 * nobody plays, or the players cannot start where it says, which is what Arrange is shut for.
 */
export function heldBack(board: Board, opened: boolean): string | null {
  if (!opened) return "Open a score to arrange it.";
  if (board.roster.length === 0) return "Pick who plays to arrange it.";
  return unseatable(board.roster, board.review.start);
}

/**
 * The run an answer setting `was` to `next` starts, in a score of `total` bars, or `null` where
 * it starts none. `ran` is the run on screen. Asked for nothing new, the rest of a first hearing
 * is arranged, with its take, and nothing else is; a request that could not be met and changed
 * nothing arranges nothing; anything else is a first hearing of the new board, take 1.
 */
export function arrangingOf(
  was: Board,
  next: Board,
  ran: Ran | null,
  unmet: string | null,
  total: number | null,
): Ran | null {
  if (ran !== null && sameAs(ran.board, next, total)) {
    if (!ran.preview || unmet !== null) return null;
    return { board: ran.board, bars: askedWindow(ran.board), take: ran.take, preview: false };
  }
  if (unmet !== null && sameAs(was, next, total)) return null;
  const window = askedWindow(next);
  const first = firstHearing(window, total);
  return { board: next, bars: first ?? window, take: 1, preview: first !== null };
}

/** What `run` arranges, said to the reader who asked for it, in a score of `total` bars. */
export function arrangingSaid(run: Ran, total: number | null): string {
  if (run.preview && run.bars !== undefined) {
    return `Arranging bars ${run.bars[0]}–${run.bars[1]} first; the rest when you say.`;
  }
  return `Arranging ${barsSaid(run.bars, total)}.`;
}

/** `run` as the job is asked for it, of the score `picked`, which is `library`'s where not `null`. */
export function requestOf(run: Ran, picked: Picked, library: string | null): ArrangeAsk {
  const { roster, choices, review } = run.board;
  return {
    // A library score is named by its id and read by the server from disk; anything else is
    // uploaded.
    library,
    text: picked.text,
    roster: [...roster],
    bars: run.bars,
    // Only starting from the score takes the choices: they stay set, unsent, for then.
    ...(review.start === "score" ? choices : NO_CHOICES),
    ...requested(review),
    take: run.take,
    name: picked.name,
    title: picked.name.replace(/\.[^.]+$/, ""),
  };
}
